import { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronRight } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { getMeasureDisplayName } from '@/lib/equip-utils'
import type { MeasureChartData } from '@/lib/equip-types'

const PASS_COLOR = 'var(--equip-pass-light)'
const FAIL_COLOR = 'var(--equip-fail-light)'

export function MeasureBar({
  measure,
  isExpanded,
  onRowClick,
  accentBg,
}: {
  measure: MeasureChartData
  isExpanded: boolean
  onRowClick: (measureName: string, measureType: string) => void
  accentBg?: string
}) {
  const [isHovered, setIsHovered] = useState(false)
  const isZeroDenom = measure.isZeroDenominator === true
  const displayPercentage = Math.round(measure.performance) // Already 0-100 scale
  const isPassing = measure.passThreshold
  const fillColor = isPassing ? PASS_COLOR : FAIL_COLOR
  // Config-driven: measure.thresholdDisplay is the user-facing target (0-100).
  const displayThreshold = measure.thresholdDisplay

  const handleClick = () => {
    onRowClick(measure.measureName, measure.measureType)
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            role="button"
            aria-expanded={isExpanded}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                handleClick()
              }
            }}
            className={cn(
              'p-3 cursor-pointer transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              isExpanded ? 'rounded-t-xl' : 'rounded-xl',
              accentBg || (isHovered || isExpanded ? 'bg-muted/40' : 'bg-transparent'),
            )}
            onHoverStart={() => setIsHovered(true)}
            onHoverEnd={() => setIsHovered(false)}
            onClick={handleClick}
          >
            <div className="flex items-center gap-4 md:gap-6">
              {/* Chevron + title */}
              <div className="w-[140px] flex-shrink-0 flex items-center gap-1.5">
                <motion.div
                  animate={{
                    opacity: isHovered || isExpanded ? 1 : 0.3,
                    rotate: isExpanded ? 90 : 0,
                    scale: isHovered && !isExpanded ? 1.4 : 1,
                  }}
                  transition={{ duration: 0.2 }}
                  className="shrink-0"
                >
                  <ChevronRight className="h-4 w-4 text-muted-foreground/60" />
                </motion.div>
                <p className={cn(
                  'text-sm font-semibold truncate',
                  isZeroDenom ? 'text-muted-foreground/60' : 'text-foreground',
                )}>
                  {getMeasureDisplayName(measure.measureName)}
                </p>
              </div>

              <div className="flex-1">
                <div className="relative flex items-center h-6">
                  <div className="absolute inset-0 bg-muted rounded-xl overflow-hidden">
                    {!isZeroDenom && (
                      <motion.div
                        className="h-full rounded-xl"
                        style={{
                          backgroundColor: fillColor,
                          opacity: 0.55,
                        }}
                        initial={{ width: '0%' }}
                        animate={{ width: `${measure.performance}%` }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                      />
                    )}

                    {!isZeroDenom && (
                      <div
                        className="absolute top-0 bottom-0 border-l-2 border-dashed border-muted-foreground/50"
                        style={{
                          left: `${displayThreshold}%`,
                        }}
                      />
                    )}
                  </div>

                  <span className={cn(
                    'relative z-10 pl-2 text-xs font-semibold',
                    isZeroDenom ? 'text-muted-foreground/60' : 'text-muted-foreground',
                  )}>
                    {isZeroDenom ? 'N/A' : `${displayPercentage}%`}
                  </span>
                </div>
              </div>

              <span className={cn(
                'text-sm font-semibold w-[50px] text-right flex-shrink-0',
                isZeroDenom ? 'text-muted-foreground/60' : 'text-foreground',
              )}>
                {isZeroDenom ? 'N/A' : `${displayPercentage}%`}
              </span>
            </div>

            {measure.dataAsOfLabel && (
              <p className="pl-6 mt-0.5 text-[11px] leading-none text-muted-foreground/70">
                {measure.dataAsOfLabel}
              </p>
            )}
          </motion.div>
        </TooltipTrigger>

        <TooltipContent className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold text-sm">{getMeasureDisplayName(measure.measureName)}</p>
            {isZeroDenom ? (
              <p className="text-xs text-muted-foreground">Eligible: 0 &middot; Not scored this quarter</p>
            ) : (
              <>
                <p className="text-xs">Rate: {displayPercentage}%</p>
                <p className="text-xs">Threshold: {displayThreshold}%</p>
                <p className="text-xs">
                  Met: {measure.met} / Eligible: {measure.eligible}
                </p>
              </>
            )}
            <p className="text-sm italic font-semibold text-white mt-2">Click to view details</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

